import { useMemo, useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import {
  hasPermission,
  hasRoutePermission,
  getPermissionsByRole,
  isAdminUser,
  getUserSpecificRole,
  isAdminUserByField,
  getUserPermissionsFromAPI,
  initializePermissionsCache
} from '@/config/permissions'
import type { UserRole, Permission } from '@/types'

export function usePermissions() {
  const { user } = useAuth()
  const [userSpecificPermissions, setUserSpecificPermissions] = useState<Permission[]>([])
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  
  // Determinar o role do usuário (suporte a múltiplos roles)
  const userRole = useMemo(() => {
    if (!user?.login) return 'usuario_basico'
    
    // 1. Verificar se o usuário é admin pelo campo is_admin (prioridade máxima)
    if (isAdminUserByField(user)) {
      return 'administrador'
    }
    
    // 2. Verificar se tem role específico configurado
    const specificRole = getUserSpecificRole(user.login)
    if (specificRole) {
      return specificRole
    }
    
    // 3. Se o usuário está na lista de administradores, forçar role de administrador
    if (isAdminUser(user.login)) {
      return 'administrador'
    }
    
    // 4. Caso contrário, usar o role do usuário ou padrão
    return user.role || 'usuario_basico'
  }, [user?.login, user?.role, user?.is_admin])
  
  // Carregar permissões específicas do usuário da API
  useEffect(() => {
    if (user?.id && !permissionsLoaded) {
      const loadUserPermissions = async () => {
        try {
            const userPerms = await getUserPermissionsFromAPI(user.id!)
            
            if (userPerms.length > 0) {
              setUserSpecificPermissions(userPerms)
            }
          
          setPermissionsLoaded(true)
        } catch (error) {
          console.error('❌ Erro ao carregar permissões do usuário:', error)
          setPermissionsLoaded(true)
        }
      }
      
      loadUserPermissions()
    }
  }, [user?.id, permissionsLoaded])
  
  // Memoizar as permissões do usuário para evitar recálculos desnecessários
  const userPermissions = useMemo(() => {
    // Se temos permissões específicas do usuário da API, usar elas
    if (userSpecificPermissions.length > 0) {
      return userSpecificPermissions
    }
    
    // Caso contrário, usar permissões baseadas no role
    return getPermissionsByRole(userRole)
  }, [userRole, userSpecificPermissions])
  
  // Função para verificar se o usuário tem uma permissão específica
  const can = (permissionId: string): boolean => {
    // Se temos permissões específicas do usuário, verificar nelas primeiro
    if (userSpecificPermissions.length > 0) {
      return userSpecificPermissions.some(permission => permission.id === permissionId)
    }
    
    // Caso contrário, usar verificação baseada em role
    return hasPermission(userRole, permissionId)
  }
  
  // Função para verificar se o usuário tem permissão para acessar uma rota
  const canAccessRoute = (route: string): boolean => {
    return hasRoutePermission(userRole, route)
  }
  
  // Função para verificar se o usuário tem qualquer uma das permissões fornecidas
  const canAny = (permissionIds: string[]): boolean => {
    return permissionIds.some(permissionId => can(permissionId))
  }
  
  // Função para verificar se o usuário tem todas as permissões fornecidas
  const canAll = (permissionIds: string[]): boolean => {
    return permissionIds.every(permissionId => can(permissionId))
  }
  
  // Função para verificar se o usuário é de um role específico
  const hasRole = (role: UserRole): boolean => {
    return userRole === role
  }
  
  // Função para verificar se o usuário é administrador
  const isAdmin = (): boolean => {
    return hasRole('administrador')
  }
  
  // Função para verificar se o usuário pode gerenciar vistorias
  const canManageVistorias = (): boolean => {
    return canAny(['vistoria_view', 'vistoria_create', 'vistoria_edit', 'vistoria_delete'])
  }
  
  // Função para verificar se o usuário pode gerenciar usuários
  const canManageUsers = (): boolean => {
    return canAny(['user_view', 'user_create', 'user_edit', 'user_delete'])
  }
  
  // Função para verificar se o usuário pode gerenciar calendários
  const canManageCalendarios = (): boolean => {
    return canAny(['calendario_view', 'calendario_create', 'calendario_edit', 'calendario_delete'])
  }
  
  // Função para verificar se o usuário pode gerenciar cardápios
  const canManageCardapios = (): boolean => {
    return canAny(['cardapio_view', 'cardapio_create', 'cardapio_edit', 'cardapio_delete'])
  }
  
  // Função para verificar se o usuário pode gerenciar geolocalização
  const canManageGeolocation = (): boolean => {
    return canAny(['geolocation_view', 'geolocation_create', 'geolocation_edit', 'geolocation_delete'])
  }
  
  // Função para verificar se o usuário pode gerenciar parâmetros
  const canManageParametros = (): boolean => {
    return canAny(['parametro_view', 'parametro_create', 'parametro_edit', 'parametro_delete'])
  }
  
  return {
    userRole,
    userPermissions,
    can,
    canAccessRoute,
    canAny,
    canAll,
    hasRole,
    isAdmin,
    canManageVistorias,
    canManageUsers,
    canManageCalendarios,
    canManageCardapios,
    canManageGeolocation,
    canManageParametros,
    // Funções de conveniência para verificar permissões específicas
    canViewDashboard: () => can('dashboard_view'),
    canViewRelatorios: () => can('relatorio_view'),
    canViewConfig: () => can('config_view'),
    canEditConfig: () => can('config_edit'),
    canViewHelp: () => can('help_view')
  }
}
